import ErrorManager from '@/common/utils/ErrorManager';
import { DemandeIntervention } from '@/models/DemandeIntervention';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState, store } from '@/plugins/store';
import dayjs from 'dayjs';
import { ActionTree } from 'vuex';
import InterventionService from '../services/InterventionService';
import { InterventionModuleAction, InterventionModuleActionTypes } from './actions-types';
import { INTERVENTION } from './mutation-types';
import { DemandeInterventionState } from './state';
import { OptionsSuiviDIModuleAction } from '@/modules/OptionsSuiviInterventions/store/actions-types';
import { EmailSuivi } from '@/modules/OptionsSuiviInterventions/store/state';
import { ContactFormState } from '@/models/ContactFormState';
import jwtDecode from 'jwt-decode';
import SiteService from '@/common/utils/SiteService';
import AccountService from '@/modules/Account/services/AccountService';
import { SocieteDTO } from '@/models/SocieteDTO';
import Holidays from 'date-holidays';

const HT_ASTREINTE= {
    societe: 'Herve Thermique',
    telephone: '0 800 500 750',
};
const ALVAZZI_ASTREINTE = {
    societe: 'Alvazzi',
    telephone: '0 800 442 884',
};

export const actions: ActionTree<DemandeInterventionState, RootState> = {
    async [InterventionModuleAction.SEND_ASK_INTERVENTION]({
        state,
        commit
    }: InterventionModuleActionTypes): Promise<void> {
        commit(INTERVENTION.SET_LOADING, true);

        const newIntervention: DemandeIntervention = {
            chantierId: state.site?.chantierId.toString() ?? '0',
            adresseSiteId: state.site?.id.toString() ?? '0',
            delaiTypeId: state.delai?.id.toString() ?? '0',
            dateAutre: state.dateAutre ? dayjs(state.dateAutre).format('YYYY-MM-DD') : '',
            dateCreation: dayjs().format('YYYY-MM-DDTHH:mm:ss.sss'),
            phaseId: state.equipement?.phaseId.toString() ?? null,
            etatFonctionnementId: state.etatEquipement?.id.toString() ?? null,
            objet: state.objet,
            commentaire: state.descriptif,
            recurrence: state.recurrente,
            suivi: state.suivi,
            contacts: state.contacts,
            contactSurSite: {
                nomPrenom: `${state.contactSurSite.firstname} ${state.contactSurSite.lastname}`,
                telephoneMobile: state.contactSurSite.telephoneMobile,
                telephoneFixe: state.contactSurSite.telephoneFixe,
                isConnectedUser: state.contactSurSite.isConnectedUser
            },
            origineDemande: 'PORTAIL'
        };

        try {
            await InterventionService.sendAskIntervention(newIntervention, state.documents).then(
                async response => {
                    commit(INTERVENTION.SET_RESPONSE, response.data);
                }
            );
            commit(INTERVENTION.SET_SUCCESS, true);
        } catch (error) {
            commit(INTERVENTION.SET_SUCCESS, false);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`,await ErrorManager.processError(error), {root: true});
        }

        commit(INTERVENTION.SET_LOADING, false);
    },

    async [InterventionModuleAction.PREFILL_CONTACTS_SUIVI]({commit, getters}: InterventionModuleActionTypes): Promise<void> {
        await store.dispatch(`optionsSuiviInterventionState/${OptionsSuiviDIModuleAction.GET_SUIVI_DI}`);
        if(getters.getInterventionContactsValue.length === 0 && getters['optionsSuiviInterventionState/isSuiviActif']){
            const emailsSuivi: EmailSuivi[] = getters['optionsSuiviInterventionState/emailsSuivi'];
            const emails = emailsSuivi?.filter(e => e.actif).map(e => e.email);
            const functionCreateContact = function(email: string) {
                return {
                    firstname: '',
                    lastname: '',
                    email: email
                } as ContactFormState;
            };
            emails?.forEach(e => commit(INTERVENTION.ADD_CONTACT, functionCreateContact(e)));
            commit(INTERVENTION.SET_SUIVI, true);
        }
    },

    async [InterventionModuleAction.PREFILL_CONNECTED_CONTACT_SUIVI]({commit}: InterventionModuleActionTypes): Promise<void> {
        if (store.state.authState.token) {
            const { sub, email }: UserToken = jwtDecode(store.state.authState.token);
            commit(INTERVENTION.ADD_CONTACT,{ firstname: sub.split(' ')[0] || '', lastname: sub.split(' ')[1] || '', email: email });
        }
    },

    async [InterventionModuleAction.SHOW_ASTREINTE_MODALE]({commit}: InterventionModuleActionTypes): Promise<void>{
        const debutJournee = dayjs().hour(8);
        const finJournee = dayjs().hour(16).minute(30);
        const now = dayjs();
        AccountService.getSocieteIds().then(async response => {
            const societesIds = response.data.map((societe: SocieteDTO) => societe.id);
            const joursFerieHTList = [];
            const joursFerieAlvazziList: any = [];
            let isJourFerie = false;
            if(SiteService.isHT(societesIds)) {
                const joursFerieHT = new Holidays('FR');
                joursFerieHTList.push(joursFerieHT.getHolidays(dayjs().year()));
            }
            if(SiteService.isAlvazzi(societesIds)) {
                const joursFerieAlvazzi = new Holidays('CH');
                joursFerieAlvazziList.push(joursFerieAlvazzi.getHolidays(dayjs().year()));
            }

            if (joursFerieHTList[0] != null && joursFerieHTList[0].filter((j: any) => j.date.substring(0, 10) === dayjs().format('YYYY-MM-DD')).length > 0) {
                commit(INTERVENTION.ADD_SOCIETE_JOUR_FERIE, [HT_ASTREINTE]);
                isJourFerie = true;
            }
            if (joursFerieAlvazziList[0] != null && joursFerieAlvazziList[0].filter((j: any) => j.date.substring(0, 10) === dayjs().format('YYYY-MM-DD')).length > 0) {
                commit(INTERVENTION.ADD_SOCIETE_JOUR_FERIE, [ALVAZZI_ASTREINTE]);
                isJourFerie = true;
            }

            if (!isJourFerie && now.isBefore(debutJournee) || now.isAfter(finJournee)) {
                if (SiteService.isHT(societesIds) && SiteService.isAlvazzi(societesIds)) {
                    commit(INTERVENTION.SET_SOCIETE_ASTREINTE, [
                        HT_ASTREINTE, ALVAZZI_ASTREINTE
                    ]);
                } else{
                    if (SiteService.isHT(societesIds)) {
                        commit(INTERVENTION.SET_SOCIETE_ASTREINTE, [HT_ASTREINTE]);
                    } else {
                        commit(INTERVENTION.SET_SOCIETE_ASTREINTE, [ALVAZZI_ASTREINTE]);
                    }
                }
            }
        });
    },
};

interface UserToken {
    sub: string;
    email: string;
}
